import React from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LiveOperations } from "@/components/LiveOperations";
import { MEDIA, COMPANY } from "@/lib/company";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Ship, Plane, Truck, Warehouse, Gauge, ShieldCheck } from "lucide-react";

const STATS = [
  { value: "2.4M+", label: "Parcels tracked" },
  { value: "180+", label: "Cities served" },
  { value: "99.2%", label: "On-time rate" },
  { value: "24/7", label: "Live operations" },
];

const PILLARS = [
  { icon: Truck, title: "Last-Mile Delivery", text: "Dense courier routes with real-time status at every doorstep handoff." },
  { icon: Warehouse, title: "Warehousing & Sort", text: "Automated sort hubs that scan, route and dispatch around the clock." },
  { icon: Ship, title: "Ocean & Intermodal", text: "Global container freight connected seamlessly to road and rail." },
  { icon: Plane, title: "Air Gateway", text: "Priority air freight for time-critical shipments across continents." },
  { icon: Gauge, title: "Real-Time Tracking", text: "Every movement timestamped into a transparent public timeline." },
  { icon: ShieldCheck, title: "Secure by Design", text: "Hashed credentials, minimal public data, and role-based access control." },
];

export default function AboutPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <Navbar />
      <section className="relative overflow-hidden">
        <img src={MEDIA.shipPort} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/92 via-slate-950/85 to-[#0B0F17]" />
        <div className="relative mx-auto max-w-5xl px-4 py-24 text-center sm:px-6">
          <p className="mb-3 text-xs font-mono uppercase tracking-[0.25em] text-orange-400">About {COMPANY.name}</p>
          <h1 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
            The network that<br /><span className="text-orange-500">never sleeps.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-slate-300">
            {COMPANY.name} moves freight and parcels across road, rail, sea and air — with live tracking that turns the black box of shipping into a transparent, real-time timeline.
          </p>
          <Button onClick={() => navigate("/")} className="mt-8 bg-orange-600 text-white hover:bg-orange-700">
            Track a shipment
          </Button>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-xl border border-white/10 bg-slate-900/60 p-6 text-center">
              <p className="text-3xl font-black text-orange-500 sm:text-4xl">{s.value}</p>
              <p className="mt-1 text-xs font-mono uppercase tracking-wider text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <h2 className="text-2xl font-black tracking-tight sm:text-3xl">What we move</h2>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p) => (
            <div key={p.title} className="rounded-xl border border-white/10 bg-slate-900/60 p-6 transition-colors hover:border-orange-500/40">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-orange-600/15 text-orange-400">
                <p.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-bold">{p.title}</h3>
              <p className="mt-1 text-sm text-slate-400">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <LiveOperations />
      <Footer />
    </div>
  );
}
